import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  computed,
  effect,
  inject,
  input,
  signal,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { RouterLink } from '@angular/router';
import { Permissions } from '@core/guard/Permissions';
import { CompanyAutoRegistration } from '@interfaces/company/company-auto-registration.response';
import { PermissionsService } from '@services/helpers/permissions.service';
import { ToastHelperService } from '@services/helpers/toast-helper.service';
import { CompanyService } from '@services/http/company.service';
import { SharedModule } from '@shared/shared.module';
import { VaultDatePipe } from '@shared/pipes/vault-date.pipe';
import { DATA_ROOM_DOCUMENT_LINK_ROUTE } from '@shared/utils/data-room-document-link.util';

/**
 * Faixa no topo da ficha da empresa cadastrada pelo Cog a partir de um documento do Data Room (PBI 3576): quando, por
 * quê, documento e empresa de origem, quem pediu, nome lido x nome na Receita e grupo econômico. "Confirmar cadastro"
 * (`CompanyAutoRegistration.REVIEW`) marca que alguém conferiu. Só aparece com `CompanyAutoRegistration.VIEW`; sem dado
 * (empresa não cadastrada pelo Cog, ou backend anterior) não mostra nada.
 */
@Component({
  selector: 'app-company-auto-registration-banner',
  imports: [SharedModule, RouterLink, VaultDatePipe],
  templateUrl: './company-auto-registration-banner.component.html',
  styleUrl: './company-auto-registration-banner.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CompanyAutoRegistrationBannerComponent {
  private readonly companyService = inject(CompanyService);
  private readonly permissions = inject(PermissionsService);
  private readonly toast = inject(ToastHelperService);
  private readonly destroyRef = inject(DestroyRef);

  readonly companyId = input.required<string>();

  protected readonly documentLinkRoute = DATA_ROOM_DOCUMENT_LINK_ROUTE;

  protected readonly canView = toSignal(
    this.permissions.hasPermission(Permissions.CompanyAutoRegistration.VIEW),
    { initialValue: false }
  );
  protected readonly canReview = toSignal(
    this.permissions.hasPermission(Permissions.CompanyAutoRegistration.REVIEW),
    { initialValue: false }
  );

  protected readonly registration = signal<CompanyAutoRegistration | null>(null);
  protected readonly reviewing = signal(false);

  /** Nome lido e nome na Receita só aparecem juntos quando os dois vieram. */
  protected readonly showNames = computed(() => {
    const data = this.registration();
    return !!data?.readCompanyName || !!data?.registryCompanyName;
  });

  constructor() {
    // Carrega quando a empresa e a permissão estão prontas (a permissão chega depois do primeiro render).
    effect(() => {
      const companyId = this.companyId();
      if (!companyId || !this.canView()) {
        this.registration.set(null);
        return;
      }
      this.load(companyId);
    });
  }

  protected confirm(): void {
    const companyId = this.companyId();
    if (!companyId || !this.canReview() || this.reviewing()) return;
    this.reviewing.set(true);
    this.companyService
      .reviewAutoRegistration(companyId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: data => {
          this.reviewing.set(false);
          if (data) this.registration.set(data);
          this.toast.showToastSuccess(
            'Cadastro confirmado',
            'A empresa deixa de aparecer como "a conferir" na lista de Clientes.'
          );
        },
        error: error => {
          this.reviewing.set(false);
          this.toast.showToastError(
            'Não foi possível confirmar o cadastro',
            error?.errors?.[0]?.message ?? 'Tente Confirmar cadastro de novo em alguns instantes.'
          );
        },
      });
  }

  private load(companyId: string): void {
    this.companyService
      .getAutoRegistration(companyId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: data => this.registration.set(data),
        // 404: empresa não foi cadastrada pelo Cog. A faixa simplesmente não aparece.
        error: () => this.registration.set(null),
      });
  }
}
